import { useMemo, useState } from 'react';
import { Icon, Input, Row } from '@efira/ui';

import type { GiftRow } from '../api/queries';
import { coins } from './labels';

/**
 * Выбор подарка: список с поиском и ценой рядом.
 *
 * ЧЕТЫРЕ РЕШЕНИЯ, КАЖДОЕ ПРОТИВ КОНКРЕТНОГО СЛУЧАЯ.
 *
 * 1. Цена стоит рядом с названием и сразу в двух единицах - монеты и деньги
 *    стримера. У конкурента пороги показаны только в монетах, и решение
 *    «дорогой это подарок или дешёвый» принимается вслепую.
 *
 * 2. Непривязываемые подарки ПОКАЗЫВАЮТСЯ и объясняют причину, а не исчезают
 *    из списка. Исчезнувшая строка - это функциональность, о которой
 *    пользователь не узнает (`_REVISION_CSS_GROUP.md`, раздел 3).
 *
 * 3. Сначала идут подарки, которые зритель может отправить прямо сейчас -
 *    порядок задаёт сервер.
 *
 * 4. Иконки берутся с НАШЕГО адреса - `cdn.efira.live`. Готовый адрес
 *    присылает сервер: где лежит хранилище, знает он, а не браузер.
 */
export interface GiftPickerProps {
  gifts: GiftRow[];
  value: string | null;
  onSelect: (giftId: string) => void;
}

export function GiftPicker({ gifts, value, onSelect }: GiftPickerProps) {
  const [query, setQuery] = useState('');

  const found = useMemo(() => {
    const needle = query.trim().toLowerCase();

    const matched =
      needle === ''
        ? gifts
        : gifts.filter(
            (gift) =>
              gift.name.toLowerCase().includes(needle) ||
              gift.id.includes(needle) ||
              String(gift.coins).startsWith(needle),
          );

    return matched.slice(0, 80);
  }, [gifts, query]);

  const hidden = gifts.length - found.length;

  return (
    <div className="flex flex-col gap-2">
      <Input
        type="search"
        size="s"
        value={query}
        placeholder="Поиск подарка или цены"
        aria-label="Поиск подарка"
        onChange={(event) => setQuery(event.target.value)}
      />

      <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto" aria-label="Подарки">
        {found.map((gift) => {
          const selected = gift.id === value;

          return (
            <li key={gift.id}>
              {/*
                СТРОКА ПОДАРКА - РОЛЬ «СТРОКА СПИСКА» (`Row`), а не кнопка.

                Прежде это была кнопка пресета с картинкой, названием,
                ценой и переносимой строкой причины внутри. У кнопки
                фиксированная высота: причина уезжала за её край
                (аудит единицы 16, находка 20 — тот же корень, что 01 и 02).

                Выбранное показывается вариантом строки и галочкой, а не
                заливкой primary: цветной текст на primary — отдельный
                разговор о контрасте, а роль «выбрано» и без него читается.
              */}
              <Row
                asChild
                size="sm"
                variant={selected ? 'outline' : 'default'}
                media={
                  gift.icon === null ? (
                    <span className="bg-border inline-block size-6 rounded" aria-hidden="true" />
                  ) : (
                    <img
                      className="size-6 rounded"
                      src={gift.icon}
                      alt=""
                      width={24}
                      height={24}
                      loading="lazy"
                    />
                  )
                }
                title={gift.name}
                description={gift.selectable ? undefined : gift.unselectableReason}
                actions={
                  <span className="flex items-center gap-2">
                    <span className="text-foreground/80 text-xs">
                      {coins(gift.coins)}
                      {gift.priceLocal === null
                        ? ''
                        : ` · ≈ ${gift.priceLocal.amount} ${gift.priceLocal.currency}`}
                    </span>
                    {selected ? <Icon name="check" size="sm" tone="accent" /> : null}
                  </span>
                }
              >
                {/* eslint-disable-next-line no-restricted-syntax -- `asChild`
                    строки требует голого элемента; содержимое вкладывает Row. */}
                <button
                  type="button"
                  disabled={!gift.selectable}
                  aria-pressed={selected}
                  // Причина недоступности читается вслух и видна при наведении:
                  // неактивная строка без объяснения - это загадка, а не запрет.
                  title={gift.unselectableReason ?? undefined}
                  aria-label={`${gift.name}, ${coins(gift.coins)}${
                    gift.selectable ? '' : `. ${gift.unselectableReason ?? ''}`
                  }`}
                  onClick={() => {
                    onSelect(gift.id);
                  }}
                />
              </Row>
            </li>
          );
        })}
      </ul>

      {hidden > 0 ? (
        <p className="text-foreground/75 text-sm">
          Показаны первые {found.length} из {gifts.length}. Уточните поиск, чтобы увидеть остальные.
        </p>
      ) : null}
    </div>
  );
}
